import {
  Card,
  CardContent,
} from "@arcadeai/design-system/components/ui/atoms/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@arcadeai/design-system/components/ui/atoms/tooltip";
import { getToolkitIcon } from "@arcadeai/design-system/metadata/toolkit-icons";
import type { JSX } from "react";
import {
  formatChipLabel,
  OnboardingToolkitChips,
} from "@/app/onboarding-first-win/components/onboarding-toolkit-chips";

type OnboardingJtbdRecapCardProps = {
  description?: string | null;
  name: string | null;
  services: string[];
};

export function OnboardingJtbdRecapCard({
  description,
  name,
  services,
}: OnboardingJtbdRecapCardProps): JSX.Element | null {
  if (!name && services.length === 0) {
    return null;
  }

  const nameClassName =
    "truncate whitespace-nowrap font-medium text-foreground text-sm leading-snug";

  let nameNode: JSX.Element | null = null;
  if (name) {
    nameNode = description ? (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger render={<p className={nameClassName} />}>
            {name}
          </TooltipTrigger>
          <TooltipContent className="max-w-xs px-0 py-0" side="right">
            <div className="flex flex-col">
              <div className="flex flex-col gap-1 px-3 py-2.5">
                <p className="font-medium text-foreground text-sm leading-snug">
                  {name}
                </p>
                <p className="text-muted-foreground text-xs leading-relaxed">
                  {description}
                </p>
              </div>
              {services.length > 0 ? (
                <div className="flex flex-col gap-1.5 border-foreground/10 border-t px-3 py-2.5 dark:border-border">
                  <p className="font-medium text-[10px] text-muted-foreground uppercase tracking-wide">
                    Toolkits
                  </p>
                  <ul className="flex flex-col gap-1">
                    {services.map((service) => {
                      const Icon = getToolkitIcon(service);
                      return (
                        <li
                          className="flex items-center gap-2 text-foreground text-xs"
                          key={service}
                        >
                          <Icon
                            aria-hidden
                            className="size-3.5 text-muted-foreground"
                          />
                          <span>{formatChipLabel(service)}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : null}
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      <p className={nameClassName} title={name}>
        {name}
      </p>
    );
  }

  return (
    <Card
      className="w-full min-w-0 gap-0 py-2 shadow-none ring-border/60"
      size="sm"
    >
      <CardContent className="flex min-w-0 flex-col gap-1.5 px-3 py-0">
        {nameNode}
        {services.length > 0 ? (
          <OnboardingToolkitChips services={services} />
        ) : null}
      </CardContent>
    </Card>
  );
}
